import { cn } from '@rocco/utils/cn';
import {
    PUBLICATION_COVER_ASPECT,
    PublicationCovers,
} from '../config/publication-covers';

import type { DigitalPublicationOption } from '../api/get-digital-publication-options.server';

interface PublicationSelectionPaneProps {
    publications: DigitalPublicationOption[];
    /**
     * Tighter rows for the mobile sheet, where this sits above the form rather
     * than beside it — the desktop pane's scale pushes the heading and the first
     * field off the first screen.
     */
    compact?: boolean;
    /**
     * The catalogue has not answered yet. Distinct from an empty list: an empty
     * pane during the fetch reads as "there is nothing", which is a failure
     * message shown to a reader whose request is still in flight.
     */
    loading?: boolean;
    /**
     * Show this series alone, large — its cover, its name and what it is —
     * rather than the list. For the modal opened from one cover in the
     * articles directory's row, where the reader has already chosen what to
     * look at. Falls back to the list when the series is not in the catalogue.
     */
    featuredNewsletterType?: string;
    className?: string;
}

/** The featured cover's width: about half the pane, the design's large cover. */
const FEATURED_COVER_CLASS = 'w-[16rem]';

/**
 * The subscribe modal's left pane: every publication on offer, each with the
 * cover of its latest issue and what it is — or, opened from one cover, that
 * one publication alone and large.
 *
 * Carries the publications rather than brand imagery, so what the reader is
 * subscribing to stays in view beside the form. On mobile the pane is
 * hidden by TwoPaneModal, so the sheet renders this above the form instead; both
 * placements share this component.
 */
export const PublicationSelectionPane = ({
    publications,
    compact = false,
    loading = false,
    featuredNewsletterType,
    className,
}: PublicationSelectionPaneProps) => {
    const featured = featuredNewsletterType
        ? publications.find(
              (publication) =>
                  publication.newsletterType === featuredNewsletterType,
          )
        : undefined;

    if (featuredNewsletterType && loading && publications.length === 0) {
        return (
            <div
                className={cn(
                    'flex size-full flex-col items-center justify-center bg-[#ebebeb] px-[2.5rem] py-[2rem]',
                    className,
                )}
                aria-busy="true"
                aria-label="Loading publication"
            >
                <div
                    className={cn(
                        PUBLICATION_COVER_ASPECT,
                        FEATURED_COVER_CLASS,
                        'animate-pulse rounded-[0.25rem] bg-black/8',
                    )}
                />
                <div className="mt-[1.75rem] h-6 w-1/2 animate-pulse rounded bg-black/8" />
                <div className="mt-3 h-4 w-4/5 animate-pulse rounded bg-black/5" />
                <div className="mt-1.5 h-4 w-3/5 animate-pulse rounded bg-black/5" />
            </div>
        );
    }

    if (featured) {
        const cover =
            PublicationCovers[featured.newsletterType] ?? featured.coverImage;
        return (
            <div
                className={cn(
                    'flex size-full flex-col items-center justify-center bg-[#ebebeb] px-[2.5rem] py-[2rem] text-center',
                    className,
                )}
                data-testid="publication-featured-pane"
            >
                {cover ? (
                    <img
                        src={cover}
                        alt=""
                        className={cn(
                            PUBLICATION_COVER_ASPECT,
                            FEATURED_COVER_CLASS,
                            'shrink-0 rounded-[0.25rem] object-cover',
                        )}
                    />
                ) : (
                    <div
                        className={cn(
                            PUBLICATION_COVER_ASPECT,
                            FEATURED_COVER_CLASS,
                            'shrink-0 rounded-[0.25rem] bg-[#e2e0dd]',
                        )}
                    />
                )}
                <p className="m-0 mt-[1.75rem] text-[1.5rem] leading-[1.15] font-medium tracking-[-0.02em] text-[#181818]">
                    {featured.title}
                </p>
                <p className="m-0 mt-[0.75rem] max-w-[24rem] text-[1.0625rem] leading-[1.45] tracking-[-0.01em] text-[#4a4a4a]">
                    {featured.description}
                </p>
            </div>
        );
    }

    if (loading && publications.length === 0) {
        return (
            <ul
                className={cn(
                    'm-0 flex size-full list-none flex-col overflow-hidden bg-[#ebebeb]',
                    compact
                        ? 'gap-4 rounded-[0.5rem] px-4 py-4'
                        : 'gap-[1.25rem] px-[1.3125rem] py-6',
                    className,
                )}
                aria-busy="true"
                aria-label="Loading publications"
            >
                {/* One row per publication we expect, so the pane holds its
                    height and the modal does not resize under the reader when
                    the real rows arrive. */}
                {[0, 1, 2, 3].map((row) => (
                    <li key={row} className="flex items-center">
                        <div
                            className={cn(
                                'shrink-0 animate-pulse rounded-[0.125rem] bg-black/8',
                                compact
                                    ? 'mr-3 h-[4.5rem] w-[3.6rem]'
                                    : 'mr-[0.8125rem] h-[6.875rem] w-[5rem]',
                            )}
                        />
                        <div className="min-w-0 flex-1">
                            <div className="h-4 w-2/5 animate-pulse rounded bg-black/8" />
                            <div className="mt-2 h-3 w-full animate-pulse rounded bg-black/5" />
                            <div className="mt-1.5 h-3 w-4/5 animate-pulse rounded bg-black/5" />
                        </div>
                    </li>
                ))}
            </ul>
        );
    }

    if (publications.length === 0) {
        return (
            <div
                className={cn(
                    'flex size-full items-center justify-center bg-[#ebebeb] p-6',
                    className,
                )}
            >
                <p className="m-0 text-center text-[0.9375rem] text-[#656565]">
                    Our publications are unavailable just now. Please try again
                    shortly.
                </p>
            </div>
        );
    }

    return (
        <ul
            className={cn(
                // Figma 3162:15486 — #ebebeb pane, rows on a 185.5px pitch
                // (154px cover + 31.5px gutter).
                //
                // Not centred: covers at their own ratio make the list taller
                // than the pane, and `justify-center` on a scroll container puts
                // the overflow above the scrollable area as well as below it —
                // the first publication loses its title off the top and no
                // amount of scrolling brings it back.
                'm-0 flex size-full list-none flex-col overflow-y-auto bg-[#ebebeb]',
                // The four rows sit within the pane's height: 110px covers on
                // a 20px gap, so the last one is not below the fold. The
                // modal's pane spreads them to its full height; where they
                // overflow, `justify-between` falls back to the top as
                // `flex-start` would, so nothing is lost off the top.
                compact
                    ? 'gap-4 rounded-[0.5rem] px-4 py-4'
                    : 'justify-between gap-[1.25rem] px-[1.3125rem] py-6',
                className,
            )}
        >
            {publications.map((publication) => {
                const cover =
                    PublicationCovers[publication.newsletterType] ??
                    publication.coverImage;
                const row = (
                    <>
                        {cover ? (
                            /*
                             * The design's cover, already cropped to this box, so
                             * `cover` has nothing left to take off it. A series
                             * with no bundled artwork falls back to its latest
                             * issue, which is taller than the box and does lose
                             * its lower edge — better than an empty slot.
                             */
                            <img
                                src={cover}
                                alt=""
                                className={cn(
                                    PUBLICATION_COVER_ASPECT,
                                    'shrink-0 rounded-[0.125rem] object-cover',
                                    compact
                                        ? 'mr-3 w-[3.6rem]'
                                        : 'mr-[0.8125rem] w-[5rem]',
                                )}
                            />
                        ) : (
                            // Keeps the text column aligned when an issue has no
                            // cover, rather than letting one row hang left.
                            <div
                                className={cn(
                                    PUBLICATION_COVER_ASPECT,
                                    'shrink-0 rounded-[0.125rem] bg-[#e2e0dd]',
                                    compact
                                        ? 'mr-3 w-[3.6rem]'
                                        : 'mr-[0.8125rem] w-[5rem]',
                                )}
                            />
                        )}
                        <div className="min-w-0 flex-1">
                            <p
                                className={cn(
                                    'm-0 leading-[1.2] font-medium tracking-[-0.02em] text-[#181818]',
                                    compact
                                        ? 'text-[1rem]'
                                        : 'text-[1.1875rem]',
                                )}
                            >
                                {publication.title}
                            </p>
                            <p
                                className={cn(
                                    'm-0 mt-1 leading-[1.35] tracking-[-0.01em] text-[#4a4a4a]',
                                    compact
                                        ? 'line-clamp-2 text-[0.8125rem]'
                                        : // Three lines is what the design allows,
                                          // and what keeps every row on the same
                                          // pitch. The copy comes from HubSpot, so
                                          // it can be longer than the design's.
                                          'line-clamp-3 text-[0.9375rem]',
                                )}
                            >
                                {publication.description}
                            </p>
                        </div>
                    </>
                );

                return (
                    <li
                        key={publication.hubspotID}
                        className="flex items-center"
                    >
                        {row}
                    </li>
                );
            })}
        </ul>
    );
};
